@let bookingContext = selectedBookingContext();
@let event = bookingContext?.event;

<article
    class="booking-search-card"
    data-testid="venue-booking-search"
    [attr.aria-busy]="isLoading()"
>
    <div
        class="booking-search-content-shell"
        [style.height.px]="contentHeight()"
    >
        <div
            #cardContent
            class="booking-search-content"
            [attr.data-card-view]="cardView()"
        >
            @let largePartyAvailability = selectedUnavailableLargePartyAvailability();
            @if (largePartyEnquiryVenue(); as enquiryVenue) {
                <section
                    class="booking-search-form booking-search-large-party-content booking-search-view"
                    data-testid="storefront-large-party-enquiry"
                >
                    @if (form.controls.date.value; as selectedDate) {
                        @if (form.controls.idealTime.value; as selectedTime) {
                            <app-enquiry-form
                                [venue]="enquiryVenue"
                                [partySize]="form.controls.partySize.value!"
                                [selectedDate]="selectedDate"
                                [selectedTime]="selectedTime"
                                [showBackButton]="true"
                                (submissionStatusChange)="enquiryStatusChange.emit($event)"
                                (backRequested)="storefrontReturnRequested.emit()"
                            >
                            </app-enquiry-form>
                        }
                    }
                </section>
            } @else if (largePartyAvailability) {
                <section
                    class="booking-search-form booking-search-large-party-content booking-search-view"
                    data-testid="storefront-large-party-unavailable"
                >
                    <h2 class="h5 fw-bold mb-0">
                        Book at {{ venue.displayName }}
                    </h2>
                    <div
                        class="booking-search-large-party-message text-center fw-semibold"
                        data-testid="storefront-large-party-message"
                        role="status"
                    >
                        @if (largePartyAvailability.venue.largePartyMessage) {
                            <span [innerHTML]="largePartyAvailability.venue.largePartyMessage"></span>
                        } @else {
                            <span>To ensure the best possible experience, please contact the venue directly to make your booking.</span>
                            @if (largePartyAvailability.venue.phoneNumber || largePartyAvailability.venue.emailAddress) {
                                <span class="d-block mt-1">
                                    @if (largePartyAvailability.venue.phoneNumber) {
                                        <a [href]="'tel:' + largePartyAvailability.venue.phoneNumber">
                                            {{ largePartyAvailability.venue.phoneNumber }}
                                        </a>
                                    }
                                    @if (largePartyAvailability.venue.phoneNumber && largePartyAvailability.venue.emailAddress) {
                                        <span> or </span>
                                    }
                                    @if (largePartyAvailability.venue.emailAddress) {
                                        <a [href]="'mailto:' + largePartyAvailability.venue.emailAddress">
                                            {{ largePartyAvailability.venue.emailAddress }}
                                        </a>
                                    }
                                </span>
                            }
                        }
                    </div>
                    <button
                        type="button"
                        class="btn btn-outline-secondary w-100"
                        data-testid="storefront-change-booking-details"
                        (click)="returnToSearch(largePartyAvailability)"
                    >
                        Choose another party size
                    </button>
                </section>
            } @else {
                <form
                    class="booking-search-form booking-search-view"
                    [formGroup]="form"
                    (ngSubmit)="submit()"
                >
                    @if (bookingContext) {
                        <section
                            class="border rounded-3 overflow-hidden bg-white"
                            [attr.data-testid]="event ? 'storefront-event-context' : 'storefront-reason-context'"
                            aria-labelledby="storefront-selected-booking-name"
                        >
                            <div class="row g-0 align-items-stretch">
                                @if (bookingContext.imageUrl; as imageUrl) {
                                    <div class="booking-search-context-image col-12 col-md-4 position-relative">
                                        <img
                                            [ngSrc]="imageUrl"
                                            fill
                                            priority
                                            class="object-fit-cover"
                                            [alt]="bookingContext.displayName"
                                            [attr.data-testid]="event ? 'storefront-event-context-image' : 'storefront-reason-context-image'"
                                        />
                                    </div>
                                }

                                <div class="col p-3 p-md-4">
                                    <div class="vstack gap-3 h-100 justify-content-center">
                                        <header>
                                            <p class="small fw-semibold text-secondary mb-1">
                                                You're booking
                                            </p>
                                            <h2
                                                id="storefront-selected-booking-name"
                                                class="h4 fw-bold mb-0"
                                            >
                                                {{ bookingContext.displayName }}
                                            </h2>
                                        </header>

                                        @if (bookingContext.description; as description) {
                                            <div
                                                class="quill-flush text-secondary"
                                                [attr.data-testid]="event ? 'storefront-event-context-description' : 'storefront-reason-context-description'"
                                            >
                                                <quill-view class="d-block" format="html" theme="bubble" [sanitize]="true" [content]="description"></quill-view>
                                            </div>
                                        }

                                        @if (event && !updatingBooking) {
                                            @if (event.recurrence | eventRecurrenceDescription; as recurrenceDescription) {
                                                <p
                                                    class="d-inline-flex align-items-center gap-2 small text-secondary mb-0"
                                                    data-testid="storefront-event-context-recurrence"
                                                >
                                                    <i class="bi bi-arrow-repeat" aria-hidden="true"></i>
                                                    <span>{{ recurrenceDescription }}</span>
                                                </p>
                                            }

                                            <div
                                                class="d-flex flex-wrap gap-3 small text-secondary"
                                                aria-label="Event details"
                                            >
                                                @if (selectedDate; as eventDate) {
                                                    <span
                                                        class="d-inline-flex align-items-center gap-2"
                                                        data-testid="storefront-event-context-date"
                                                    >
                                                        <img
                                                            ngSrc="assets/calendar_small.svg"
                                                            width="20"
                                                            height="20"
                                                            alt=""
                                                            aria-hidden="true"
                                                        />
                                                        <span>
                                                            <span class="visually-hidden">Date: </span>
                                                            {{ eventDate | date:'EEEE d MMMM' }}
                                                        </span>
                                                    </span>

                                                    <span
                                                        class="d-inline-flex align-items-center gap-2"
                                                        data-testid="storefront-event-context-time"
                                                    >
                                                        <img
                                                            ngSrc="assets/clock_small.svg"
                                                            width="20"
                                                            height="20"
                                                            alt=""
                                                            aria-hidden="true"
                                                        />
                                                        <span>
                                                            <span class="visually-hidden">Time: </span>
                                                            @if (event.recurrence.startTime; as startTime) {
                                                                {{ startTime.dateTimeOnDate(eventDate) | date:'shortTime' }}
                                                                @if (event.recurrence.endTime; as endTime) {
                                                                    @if (!startTime.equals(endTime)) {
                                                                        &ndash; {{ endTime.dateTimeOnDate(eventDate) | date:'shortTime' }}
                                                                    }
                                                                }
                                                            } @else {
                                                                All day
                                                            }
                                                        </span>
                                                    </span>
                                                }

                                                <span
                                                    class="d-inline-flex align-items-start gap-2"
                                                    data-testid="storefront-event-context-charges"
                                                >
                                                    <img
                                                        ngSrc="assets/deposit_small.svg"
                                                        width="20"
                                                        height="20"
                                                        alt=""
                                                        aria-hidden="true"
                                                    />
                                                    <span class="vstack gap-1">
                                                        <span class="visually-hidden">Charges: </span>
                                                        @if (event.depositAmount; as depositAmount) {
                                                            <span>
                                                                {{ null | currencyUnit : depositAmount : venue.currencyCode }}
                                                                per person
                                                            </span>
                                                        }
                                                        @if (event.depositFlatFeeAmount; as depositFlatFeeAmount) {
                                                            <span>
                                                                {{ null | currencyUnit : depositFlatFeeAmount : venue.currencyCode }}
                                                                per booking
                                                            </span>
                                                        }
                                                        @if (event.cancellationChargeAmount; as cancellationChargeAmount) {
                                                            <span>
                                                                {{ null | currencyUnit : cancellationChargeAmount : venue.currencyCode }}
                                                                cancellation charge per person
                                                            </span>
                                                        }
                                                        @if (event.cancellationChargePartyAmount; as cancellationChargePartyAmount) {
                                                            <span>
                                                                {{ null | currencyUnit : cancellationChargePartyAmount : venue.currencyCode }}
                                                                cancellation charge per booking
                                                            </span>
                                                        }
                                                        @if (!bookingContext.hasCharges) {
                                                            <span>No charges</span>
                                                        }
                                                    </span>
                                                </span>
                                            </div>
                                        }
                                    </div>
                                </div>
                            </div>
                        </section>
                    } @else {
                        <h2 class="h5 fw-bold mb-0">
                            {{ updatingBooking ? 'Booking at' : 'Book at' }} {{ venue.displayName }}
                        </h2>
                    }

                    @if (availability(); as availability) {
                        @let selectedPartySize = form.controls.partySize.value;
                        @let minLargePartySize = availability.largePartySizeThreshold(selectedDate, initialValues?.eventId);
                        <div class="booking-search-fields d-flex flex-column flex-md-row gap-md-0">
                            <div class="booking-search-field col-md-3 col-lg-4">
                                <label
                                    class="form-label"
                                    for="storefront-party-size"
                                >
                                    Party Size
                                </label>
                                <div class="booking-search-control position-relative">
                                    <img
                                        class="booking-search-input-icon flex-shrink-0"
                                        src="assets/people/normal.svg"
                                        width="24"
                                        height="24"
                                        alt=""
                                        aria-hidden="true"
                                    />
                                    @if (selectedPartySize !== null) {
                                        <span
                                            class="d-inline d-md-none d-lg-inline fw-semibold"
                                            data-testid="storefront-party-size-full-label"
                                            aria-hidden="true"
                                        >
                                            @if (selectedPartySize === minLargePartySize) {
                                                @if (availability.venue.showLargerPartiesUnreservable) {
                                                    Larger parties unreservable
                                                } @else {
                                                    {{ selectedPartySize }}+
                                                }
                                            } @else {
                                                {{ selectedPartySize | i18nPlural: partySizePluralMapping }}
                                            }
                                        </span>
                                        <span
                                            class="d-none d-md-inline d-lg-none fw-semibold"
                                            data-testid="storefront-party-size-compact-label"
                                            aria-hidden="true"
                                        >
                                            {{ selectedPartySize }}
                                        </span>
                                    } @else {
                                        <span
                                            class="fw-semibold text-secondary"
                                            data-testid="storefront-party-size-placeholder"
                                            aria-hidden="true"
                                        >
                                            Choose party size
                                        </span>
                                    }
                                    <select
                                        id="storefront-party-size"
                                        class="form-select booking-search-party-size-select position-absolute top-0 start-0 w-100 h-100"
                                        formControlName="partySize"
                                        (change)="partySizeEdited()"
                                        data-testid="storefront-party-size"
                                        required
                                        aria-required="true"
                                        [attr.aria-invalid]="form.controls.partySize.invalid
                                            && form.controls.partySize.touched"
                                        [attr.aria-describedby]="form.controls.partySize.invalid
                                            && form.controls.partySize.touched
                                            ? 'storefront-party-size-error'
                                            : null"
                                    >
                                        @if (selectedPartySize === null) {
                                            <option
                                                class="text-dark"
                                                [ngValue]="null"
                                                disabled
                                            >
                                                Choose party size
                                            </option>
                                        }
                                        @for (size of possiblePartySizes(); track size) {
                                            <option
                                                class="text-dark"
                                                [ngValue]="size"
                                            >
                                                @if (size === minLargePartySize) {
                                                    @if (availability.venue.showLargerPartiesUnreservable) {
                                                        Larger parties unreservable
                                                    } @else {
                                                        {{ size }}+
                                                    }
                                                } @else {
                                                    {{ size | i18nPlural: partySizePluralMapping }}
                                                }
                                            </option>
                                        }
                                    </select>
                                </div>
                                @if (form.controls.partySize.invalid && form.controls.partySize.touched) {
                                    <div
                                        id="storefront-party-size-error"
                                        class="invalid-feedback d-block"
                                        data-testid="storefront-party-size-error"
                                    >
                                        Please choose a party size.
                                    </div>
                                }
                            </div>

                            <div class="booking-search-field booking-search-date-field col-md col-lg-4">
                                <label
                                    class="form-label"
                                    id="storefront-date-label"
                                    for="storefront-date"
                                >
                                    Date
                                </label>
                                <input
                                    #datePicker="ngbDatepicker"
                                    class="booking-search-datepicker-anchor"
                                    formControlName="date"
                                    ngbDatepicker
                                    container="body"
                                    datepickerClass="storefront-booking-datepicker"
                                    navigation="arrows"
                                    placement="bottom-start"
                                    tabindex="-1"
                                    aria-hidden="true"
                                    [minDate]="earliestDate"
                                    [markDisabled]="isDateDisabled"
                                    (navigate)="onDatepickerNavigate($event)"
                                    (dateSelect)="searchDateEdited($event)"
                                />
                                <button
                                    id="storefront-date"
                                    type="button"
                                    class="booking-search-control booking-search-button text-start fw-semibold"
                                    data-testid="storefront-date"
                                    aria-labelledby="storefront-date-label storefront-date-value"
                                    aria-haspopup="dialog"
                                    [attr.aria-expanded]="datePicker.isOpen()"
                                    [disabled]="updatingBooking?.isDateRestricted ?? false"
                                    (click)="datePicker.toggle()"
                                >
                                    <img
                                        class="booking-search-input-icon flex-shrink-0"
                                        src="assets/dropdown-calendar.svg"
                                        width="24"
                                        height="24"
                                        alt=""
                                        aria-hidden="true"
                                    />
                                    <span id="storefront-date-value">
                                        {{ selectedDate | date:'EEE d MMM' }}
                                    </span>
                                    <i class="bi bi-chevron-down booking-search-chevron ms-auto" aria-hidden="true"></i>
                                </button>
                            </div>

                            <div class="booking-search-field col-md col-lg-4">
                                <label
                                    class="form-label"
                                    for="storefront-ideal-time"
                                >
                                    Ideal Time
                                </label>
                                <div class="booking-search-control">
                                    <img
                                        class="booking-search-input-icon flex-shrink-0"
                                        src="assets/dropdown-clock.svg"
                                        width="24"
                                        height="24"
                                        alt=""
                                        aria-hidden="true"
                                    />
                                    <select
                                        id="storefront-ideal-time"
                                        class="form-select"
                                        formControlName="idealTime"
                                        (change)="idealTimeEdited()"
                                        data-testid="storefront-ideal-time"
                                        [compareWith]="compareDates"
                                    >
                                        @if (event && idealTimeOptions(availability).length === 0) {
                                            <option [ngValue]="null" disabled>
                                                No times available
                                            </option>
                                        }
                                        @for (time of idealTimeOptions(availability); track time.getTime()) {
                                            <option [ngValue]="time">
                                                {{ time | date:'shortTime' }}
                                            </option>
                                        }
                                    </select>
                                </div>
                            </div>
                        </div>

                        @if (venue.hasAccessibilityQuestions() && !updatingBooking) {
                            <fieldset class="booking-requirements">
                                <legend class="visually-hidden">Booking requirements</legend>
                                @if (venue.wheelchairAccessibleQuestionEnabled) {
                                    <label class="booking-requirement">
                                        <input
                                            type="checkbox"
                                            class="form-check-input flex-shrink-0 m-0"
                                            formControlName="requiresWheelchairAccess"
                                            data-testid="storefront-wheelchair-access"
                                        />
                                        <img
                                            class="booking-requirement-icon flex-shrink-0"
                                            src="assets/wheelchair_friendly.svg"
                                            width="24"
                                            height="24"
                                            alt=""
                                            aria-hidden="true"
                                        />
                                        <span>Wheelchair accessible seating</span>
                                    </label>
                                }
                                @if (venue.dogFriendlyQuestionEnabled) {
                                    <label class="booking-requirement">
                                        <input
                                            type="checkbox"
                                            class="form-check-input flex-shrink-0 m-0"
                                            formControlName="requiresDogFriendly"
                                            data-testid="storefront-dog-friendly"
                                        />
                                        <img
                                            class="booking-requirement-icon flex-shrink-0"
                                            src="assets/dog_friendly.svg"
                                            width="24"
                                            height="24"
                                            alt=""
                                            aria-hidden="true"
                                        />
                                        <span>Dog friendly</span>
                                    </label>
                                }
                                @if (venue.pramAccessibleQuestionEnabled) {
                                    <label class="booking-requirement">
                                        <input
                                            type="checkbox"
                                            class="form-check-input flex-shrink-0 m-0"
                                            formControlName="requiresPramAccessible"
                                            data-testid="storefront-pram-accessible"
                                        />
                                        <img
                                            class="booking-requirement-icon flex-shrink-0"
                                            src="assets/pram_friendly.svg"
                                            width="24"
                                            height="24"
                                            alt=""
                                            aria-hidden="true"
                                        />
                                        <span>Pram accessible</span>
                                    </label>
                                }
                                @if (venue.highchairFriendlyQuestionEnabled) {
                                    <label class="booking-requirement">
                                        <input
                                            type="checkbox"
                                            class="form-check-input flex-shrink-0 m-0"
                                            formControlName="requiresHighchairFriendly"
                                            data-testid="storefront-highchair-friendly"
                                        />
                                        <img
                                            class="booking-requirement-icon flex-shrink-0"
                                            src="assets/highchair_friendly.svg"
                                            width="24"
                                            height="24"
                                            alt=""
                                            aria-hidden="true"
                                        />
                                        <span>Highchair friendly</span>
                                    </label>
                                }
                            </fieldset>
                        }
                    }

                    @if (errorMessage(); as errorMessage) {
                        <div class="alert alert-light border mb-0" role="alert">
                            <div class="d-flex flex-column flex-sm-row gap-2 align-items-sm-center justify-content-between">
                                <span>{{ errorMessage }}</span>
                                @if (!venueNotInService()) {
                                    <button
                                        type="button"
                                        class="btn btn-outline-secondary btn-sm flex-shrink-0"
                                        (click)="retry()"
                                    >
                                        Try again
                                    </button>
                                }
                            </div>
                        </div>
                    } @else if (!availability()) {
                        <div class="booking-search-loading" role="status">
                            <span class="spinner-border spinner-border-sm" aria-hidden="true"></span>
                            <span>Loading availability</span>
                        </div>
                    }

                    <button
                        type="submit"
                        class="btn btn-primary w-100"
                        data-testid="storefront-search-availability"
                        [disabled]="!availability()
                            || isLoading()
                            || !!errorMessage()
                            || form.controls.date.invalid
                            || form.controls.idealTime.invalid
                            || selectedPartyCannotBeSearched()"
                    >
                        @if (isLoading() && availability()) {
                            <span class="spinner-border spinner-border-sm me-2" aria-hidden="true"></span>
                        }
                        Search Availability
                    </button>
                </form>
            }
        </div>
    </div>
</article>
